import { mixed, object as yupObject, string } from 'yup';
import { DEFAULT_BILLING_COUNTRY, isValidCountryCode } from 'lib/countries';
import {
  isValidPostalCode,
  POSTAL_CODE_FORMAT_MESSAGE,
  POSTAL_CODE_MAX_LENGTH,
} from 'lib/postalCode';
import { isValidPassword, notTooGuessable, type PaymentType } from '@elevator/ui';
import type { CheckoutValues } from 'types/payments';

export const requiredBillingField = (message: string) =>
  string().trim().required(message);

/** Stripe card + billing fields shared by submit and campaign checkout. */
export const stripeBillingValidationSchema = {
  cardInfo: mixed().test(
    'ValidCardInfo',
    'Please enter a valid card',
    (value: unknown) => {
      const card = value as { complete?: boolean; error?: unknown } | undefined;
      return Boolean(card && card.complete && !card.error);
    },
  ),
  nameOnCard: requiredBillingField('This field is required'),
  postalCode: requiredBillingField('Postal code is required')
    .max(
      POSTAL_CODE_MAX_LENGTH,
      `Postal code must be at most ${POSTAL_CODE_MAX_LENGTH} characters`,
    )
    .test('postal-format', POSTAL_CODE_FORMAT_MESSAGE, (value) =>
      isValidPostalCode(value),
    ),
  country: requiredBillingField('Country is required').test(
    'iso-country',
    'Select a valid country',
    (value) => Boolean(value && isValidCountryCode(value)),
  ),
};

const stripeBillingOnlySchema = yupObject({
  nameOnCard: stripeBillingValidationSchema.nameOnCard,
  postalCode: stripeBillingValidationSchema.postalCode,
  country: stripeBillingValidationSchema.country,
});

export const isStripeBillingValid = (values: CheckoutValues): boolean => {
  try {
    stripeBillingOnlySchema.validateSync(values, { abortEarly: true });
    return true;
  } catch {
    return false;
  }
};

export const isStripeCardComplete = (cardInfo: unknown): boolean => {
  const card = cardInfo as { complete?: boolean; error?: unknown } | undefined;
  return Boolean(card?.complete && !card?.error);
};

/** Whether Stripe checkout fields (card, billing, reCAPTCHA) are ready to submit. */
export const canSubmitStripeCheckout = (values: CheckoutValues): boolean => {
  return (
    isStripeCardComplete(values.cardInfo) &&
    isStripeBillingValid(values) &&
    Boolean(values.recaptcha)
  );
};

/** Shared submit readiness for checkout flows (submit + campaign). */
export const canSubmitCheckout = (
  paymentType: PaymentType,
  values: CheckoutValues,
  isValid = false,
): boolean => {
  if (paymentType === 'PayPal') return true;

  if (paymentType === 'Free') {
    return Boolean(values.recaptcha);
  }

  if (paymentType === 'Stripe') {
    return canSubmitStripeCheckout(values);
  }

  return isValid;
};

export const recaptchaValidationSchema = {
  recaptcha: string().required(),
};

/** Guest account fields on campaign checkout. */
export const campaignAccountValidationSchema = {
  firstName: string().required('First Name is required'),
  lastName: string().required('Last Name is required'),
  email: string()
    .email('Please enter a valid email')
    .test(
      'noWhiteSpace',
      'Emails cannot contain spaces',
      (value) => !/\s/.test(value ?? ''),
    )
    .required('This field is required'),
  username: string()
    .required('This field is required')
    .test(
      'noWhiteSpace',
      'Usernames cannot contain spaces',
      (value) => !/\s/.test(value ?? ''),
    ),
  password: string()
    .required('This field is required')
    .test('requirements', 'Password requirements not met', (value: string) => {
      if (!value) return false;
      return isValidPassword(value);
    })
    .test('notTooGuessable', 'password is too guessable', (value: string) => {
      if (!value) return false;
      return notTooGuessable(value);
    }),
  confirmPassword: string()
    .test('passwordsMatch', 'Passwords must match', function (value) {
      if (!value) return false;
      return value === this.parent.password;
    })
    .required('This field is required'),
};

export function getStripeBillingInitialValues(): Pick<
  CheckoutValues,
  'cardInfo' | 'nameOnCard' | 'postalCode' | 'country' | 'recaptcha'
> {
  return {
    cardInfo: {},
    nameOnCard: '',
    postalCode: '',
    country: DEFAULT_BILLING_COUNTRY,
    recaptcha: '',
  };
}
